import { useEffect, useState } from "react";
import api, { euro, pctIt } from "@/lib/api";
import { toast } from "sonner";
import { LongTextarea } from "@/components/LongText";
import { EuroInput } from "@/components/EuroInput";
import { DeleteBtn } from "@/components/FinDelete";
import { Wallet, UserCog, Percent, FileSpreadsheet, ShieldCheck, Plus, X } from "lucide-react";

const RUOLI = [["acquisitore", "Acquisitore"], ["venditore", "Venditore"], ["direttore", "Direttore team"], ["segnalatore", "Segnalatore"]];
const RL = Object.fromEntries(RUOLI);

// Formatta "YYYY-MM-DD" -> "gg/mm/aaaa" senza passare da Date() (niente scostamenti di fuso orario).
const fmtIt = (s) => { if (!s) return ""; const [y, m, d] = String(s).slice(0, 10).split("-"); return (d && m && y) ? `${d}/${m}/${y}` : s; };
const oggiISO = () => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`; };
// Ultima data "valida dal" registrata per un ruolo nello storico.
const quoteDalRuolo = (storico, ruolo) => {
  const es = (storico || []).filter((e) => e.ruolo === ruolo);
  if (!es.length) return null;
  es.sort((a, b) => String(a.at || a.dal || "").localeCompare(String(b.at || b.dal || "")));
  return es[es.length - 1].dal || null;
};
// Data complessiva "valide dal" = la più recente tra i ruoli attualmente attivi.
const quoteDalComplessiva = (u) => {
  const ds = RUOLI.map(([k]) => (u.quote_ruolo[k] != null ? quoteDalRuolo(u.quote_storico, k) : null)).filter(Boolean);
  if (!ds.length) return null;
  ds.sort();
  return ds[ds.length - 1];
};

function ApprovatoreCard() {
  const [cfg, setCfg] = useState(null);
  const [users, setUsers] = useState([]);
  const load = () => api.get("/direzione/approvazioni/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/direzione/soci").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const save = async (patch) => {
    try { const r = await api.put("/direzione/approvazioni/config", patch); setCfg(r.data); toast.success("Impostazione salvata"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  if (!cfg) return <div className="skeleton h-24 rounded-2xl" />;
  return (
    <div className="card-immo p-4 mb-6" data-testid="approvatore-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-3"><ShieldCheck size={14} /> Approvatore delle spese</div>
      <p className="text-[11px] text-[var(--muted)] mb-3">Tutte le spese nuove vanno approvate da questa persona prima di entrare in "Da pagare" o "Spese". Finché non scegli un approvatore, le spese entrano direttamente (nessun blocco).</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm">Approvatore
          <select data-testid="appr-approvatore" value={cfg.approvatore_user_id || ""} onChange={(e) => save({ approvatore_user_id: e.target.value || null })}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--border)] text-sm">
            <option value="">— nessuno —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </label>
        <label className="text-sm">Sostituto (quando assente)
          <select data-testid="appr-sostituto" value={cfg.sostituto_user_id || ""} onChange={(e) => save({ sostituto_user_id: e.target.value || null })}
            className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--border)] text-sm">
            <option value="">— nessuno —</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
          </select>
        </label>
        <label className="text-sm">Assente dal
          <input data-testid="appr-dal" type="date" value={cfg.assente_dal || ""} onChange={(e) => save({ assente_dal: e.target.value || null })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        </label>
        <label className="text-sm">Assente al
          <input data-testid="appr-al" type="date" value={cfg.assente_al || ""} onChange={(e) => save({ assente_al: e.target.value || null })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
        </label>
      </div>
    </div>
  );
}

function QuoteCard() {
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState({});
  const [storico, setStorico] = useState(null);
  const load = () => api.get("/direzione/utenti-quote").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const salva = async (u) => {
    const q = draft[u.id] || {};
    const quote = {}; RUOLI.forEach(([k]) => { if (q[k] !== undefined && q[k] !== "") quote[k] = Number(q[k]); });
    if (!Object.keys(quote).length) return toast.error("Inserisci almeno una quota");
    const dalUsato = q.dal || oggiISO();
    try {
      await api.put(`/direzione/utenti/${u.id}/quote`, { quote, dal: dalUsato });
      toast.success("Quote salvate");
      // Lascia nei campi i valori appena salvati e mostra la data effettiva (oggi se non indicata).
      setDraft({ ...draft, [u.id]: { ...q, dal: dalUsato } });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const set = (uid, k, v) => setDraft({ ...draft, [uid]: { ...(draft[uid] || {}), [k]: v } });
  return (
    <div className="mb-6" data-testid="quote-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2"><Percent size={14} /> Quote provvigionali per collaboratore</div>
      <p className="text-[11px] text-[var(--muted)] mb-3">Quota % diversa per ruolo nella pratica. Una nuova % vale dalla data scelta in poi e non tocca le pratiche già maturate.</p>
      <div className="space-y-2">
        {users.map((u) => {
          const attuali = RUOLI.filter(([k]) => u.quote_ruolo[k] != null).map(([k, l]) => `${l} ${pctIt(u.quote_ruolo[k])}`).join(" · ");
          const dalCompl = quoteDalComplessiva(u);
          return (
          <div key={u.id} data-testid={`quota-user-${u.id}`} className="card-immo p-4">
            <div className="font-semibold text-sm mb-2">{u.nome}</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 items-end">
              {RUOLI.map(([k, lbl]) => (
                <label key={k} className="text-[11px] text-[var(--muted)]">{lbl}
                  <div className="flex items-center gap-1">
                    <input data-testid={`quota-${u.id}-${k}`} type="number" step="0.5" placeholder={u.quote_ruolo[k] ?? "—"}
                      value={(draft[u.id] || {})[k] ?? ""} onChange={(e) => set(u.id, k, e.target.value)}
                      className="w-full mt-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
                    <span className="text-xs">%</span>
                  </div>
                </label>
              ))}
              <label className="text-[11px] text-[var(--muted)]">Valida dal
                <input data-testid={`quota-${u.id}-dal`} type="date" value={(draft[u.id] || {}).dal ?? ""} onChange={(e) => set(u.id, "dal", e.target.value)} className="w-full mt-1 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
              </label>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-[var(--muted)]" data-testid={`quota-attuali-${u.id}`}>
                Attuali: {attuali || "nessuna"}{attuali && dalCompl ? ` – valide dal ${fmtIt(dalCompl)}` : ""}
              </div>
              <div className="flex items-center gap-3">
                {(u.quote_storico || []).length > 0 && (
                  <button data-testid={`quota-storico-${u.id}`} onClick={() => setStorico(u)} className="text-xs text-[#FC82ED] font-semibold hover:underline">Storico</button>
                )}
                <button data-testid={`quota-save-${u.id}`} onClick={() => salva(u)} className="px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold">Salva quote</button>
              </div>
            </div>
          </div>
          );
        })}
      </div>
      {storico && <StoricoQuoteModal user={storico} onClose={() => setStorico(null)} />}
    </div>
  );
}

function StoricoQuoteModal({ user, onClose }) {
  const righe = [...(user.quote_storico || [])].sort((a, b) => String(b.at || b.dal || "").localeCompare(String(a.at || a.dal || "")));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="quota-storico-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold text-sm">Storico quote — {user.nome}</div>
          <button data-testid="quota-storico-close" onClick={onClose} className="p-1 rounded-lg hover:bg-slate-100"><X size={16} /></button>
        </div>
        {righe.length === 0 ? <div className="text-sm text-[var(--muted)] py-4 text-center">Nessuna versione registrata.</div> : (
          <div className="space-y-2">
            {righe.map((e, i) => (
              <div key={i} data-testid={`quota-storico-riga-${i}`} className="flex items-center justify-between text-sm border-b border-[var(--border)] last:border-0 py-2">
                <div><span className="font-medium">{RL[e.ruolo] || e.ruolo}</span> <span className="mono">{pctIt(e.pct)}</span></div>
                <div className="text-right text-[11px] text-[var(--muted)]">valide dal <b>{fmtIt(e.dal)}</b>{e.by ? <> · inserita da {e.by}</> : null}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function CollaboratoriCard() {
  const [provv, setProvv] = useState([]);
  const [users, setUsers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [rows, setRows] = useState([]);
  const load = () => api.get("/direzione/provvigioni-list").then((r) => setProvv(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/direzione/utenti-quote").then((r) => setUsers(r.data)).catch(() => {}); }, []);
  const open = (p) => { setEdit(p); setRows((p.collaboratori || []).map((c) => ({ ...c }))); };
  const salva = async () => {
    try {
      await api.put(`/direzione/provvigioni/${edit.id}/collaboratori`, { collaboratori: rows.map((r) => ({ user_id: r.user_id, ruolo: r.ruolo, pct: r.pct === "" ? undefined : r.pct, nota: r.nota })) });
      toast.success("Collaboratori salvati"); setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="mb-6" data-testid="collaboratori-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2"><UserCog size={14} /> Collaboratori per pratica</div>
      <p className="text-[11px] text-[var(--muted)] mb-3">Assegna i collaboratori (con ruolo e %) a ogni incasso. Le % di default vengono dalle quote; un'eccezione richiede una nota.</p>
      <div className="space-y-2">
        {provv.length === 0 && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessuna pratica/incasso presente.</div>}
        {provv.map((p) => (
          <div key={p.id} data-testid={`provv-${p.id}`} className="card-immo p-4 flex items-center justify-between gap-2 flex-wrap">
            <div><div className="text-sm font-semibold">{p.immobile_titolo || p.cliente || "Pratica"}</div>
              <div className="text-xs text-[var(--muted)]">{euro(p.imponibile)} · {p.stato} · {(p.collaboratori || []).length} collaboratori</div></div>
            <div className="flex items-center gap-1.5">
              {p.stato !== "incassata" && (
                <select data-testid={`provv-stato-${p.id}`} value={p.stato_agenzia || "attesa"} onChange={async (e) => { try { await api.post(`/direzione/provvigioni/${p.id}/stato`, { stato: e.target.value }); toast.success("Stato aggiornato"); load(); } catch (err) { toast.error(err.response?.data?.detail || "Errore"); } }} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-xs">
                  <option value="attesa">Attesa</option>
                  <option value="maturata">Maturata</option>
                </select>
              )}
              {p.stato === "incassata" && <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Incassata</span>}
              <button data-testid={`provv-edit-${p.id}`} onClick={() => open(p)} className="px-3 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-semibold">Collaboratori</button>
            </div>
          </div>
        ))}
      </div>
      {edit && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="collab-modal">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setEdit(null)} />
          <div className="relative bg-white rounded-2xl border border-[var(--border)] w-full max-w-lg p-5 max-h-[85vh] overflow-y-auto">
            <div className="font-display font-extrabold text-lg mb-1">{edit.immobile_titolo || "Pratica"}</div>
            <p className="text-xs text-[var(--muted)] mb-3">Imponibile {euro(edit.imponibile)}. I pagamenti ai collaboratori si generano quando l'incasso è confermato.</p>
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center mb-2">
                <select data-testid={`collab-user-${i}`} value={r.user_id || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, user_id: e.target.value } : x))} className="col-span-5 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm">
                  <option value="">— collaboratore —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.nome}</option>)}
                </select>
                <select data-testid={`collab-ruolo-${i}`} value={r.ruolo || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, ruolo: e.target.value } : x))} className="col-span-4 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm">
                  <option value="">— ruolo —</option>
                  {RUOLI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
                <input data-testid={`collab-pct-${i}`} type="number" step="0.5" placeholder="%" value={r.pct ?? ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, pct: e.target.value } : x))} className="col-span-2 px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
                <button onClick={() => setRows(rows.filter((_, j) => j !== i))} className="col-span-1 text-red-500"><X size={16} /></button>
                {r.pct !== "" && r.pct != null && (
                  <LongTextarea data-testid={`collab-nota-${i}`} placeholder="Nota (obbligatoria se cambi la % di default)" value={r.nota || ""} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, nota: e.target.value } : x))} className="col-span-12 w-full px-2 py-1.5 rounded-lg border border-[var(--border)] text-xs" />
                )}
              </div>
            ))}
            <button data-testid="collab-add" onClick={() => setRows([...rows, { user_id: "", ruolo: "", pct: "", nota: "" }])} className="flex items-center gap-1 text-sm text-[#FC82ED] font-semibold mb-3"><Plus size={14} /> Aggiungi collaboratore</button>
            <div className="flex gap-2">
              <button onClick={() => setEdit(null)} className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">Annulla</button>
              <button data-testid="collab-save" onClick={salva} className="flex-1 py-2.5 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold">Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PayoutCard() {
  const mNow = new Date().toISOString().slice(0, 7);
  const [mese, setMese] = useState(mNow);
  const [d, setD] = useState(null);
  const load = () => api.get(`/direzione/provvigioni-collaboratori?mese=${mese}`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [mese]);
  const paga = async (id) => { try { await api.post(`/direzione/provvigioni-collaboratori/${id}/paga`, { metodo: "bonifico" }); toast.success("Segnata come pagata"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const exportXlsx = async () => {
    try {
      const res = await api.get(`/direzione/provvigioni-collaboratori/export?mese=${mese}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `provvigioni_${mese}.xlsx`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("Errore export"); }
  };
  return (
    <div data-testid="payout-card">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)]"><Wallet size={14} /> Provvigioni da pagare ai collaboratori</div>
        <div className="flex items-center gap-2">
          <input data-testid="payout-mese" type="month" value={mese} onChange={(e) => setMese(e.target.value)} className="px-2 py-1.5 rounded-lg border border-[var(--border)] text-sm" />
          <button data-testid="payout-export" onClick={exportXlsx} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#111111] text-white text-xs font-semibold"><FileSpreadsheet size={13} /> Excel</button>
        </div>
      </div>
      {!d ? <div className="skeleton h-24 rounded-2xl" /> : (
        <>
          <div className="text-sm text-[var(--muted)] mb-2">Da pagare: <b className="text-[#111111]">{euro(d.totali.da_pagare)}</b> · Pagato: {euro(d.totali.pagato)}</div>
          <div className="space-y-2">
            {d.voci.length === 0 && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessuna provvigione per questo mese.</div>}
            {d.voci.map((v) => (
              <div key={v.id} data-testid={`payout-${v.id}`} className="card-immo p-4 flex items-center justify-between gap-2 flex-wrap">
                <div><div className="text-sm font-semibold">{v.nome} · {RL[v.ruolo] || v.ruolo} ({pctIt(v.pct)})</div>
                  <div className="text-xs text-[var(--muted)]">{v.pratica || "—"} · {euro(v.importo)}</div></div>
                {v.stato === "pagato"
                  ? <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">Pagata {v.data_pagamento}</span>
                  : v.stato === "attesa_incasso"
                  ? <span data-testid={`payout-attesa-${v.id}`} className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-slate-100 text-slate-500 border border-slate-200">Attesa incasso agenzia</span>
                  : <button data-testid={`payout-paga-${v.id}`} onClick={() => paga(v.id)} className="px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold">Pagata</button>}
                <DeleteBtn tipo="provvigione_collaboratore" id={v.id} label={`Provvigione ${v.nome} – ${euro(v.importo)}`} onDone={load} />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function DirezioneProvvigioni() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-6 fade-up" data-testid="direzione-provvigioni-page">
      <h1 className="font-display text-2xl font-extrabold mb-1">Provvigioni & Approvazioni</h1>
      <p className="text-sm text-[var(--muted)] mb-6">Approvatore delle spese, quote dei collaboratori e provvigioni da pagare. Sezione riservata ai Soci.</p>
      <ApprovatoreCard />
      <QuoteCard />
      <CollaboratoriCard />
      <PayoutCard />
      <FattureCard />
      <CassaCard />
      <ScadenziarioCard />
    </div>
  );
}

function CassaCard() {
  const [saldo, setSaldo] = useState(null);
  const [prev, setPrev] = useState(null);
  const load = () => { api.get("/direzione/cassa/saldo").then((r) => setSaldo(r.data)).catch(() => {}); api.get("/direzione/cassa/previsione").then((r) => setPrev(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);
  const salva = async () => { try { await api.put("/direzione/cassa/saldo", { saldo_attuale: Number(saldo.saldo_attuale) || 0, soglia_allarme: Number(saldo.soglia_allarme) || 0 }); toast.success("Cassa aggiornata"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  if (!saldo) return <div className="skeleton h-24 rounded-2xl mt-6" />;
  return (
    <div className="mt-6" data-testid="cassa-card">
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)] mb-2"><Wallet size={14} /> Cassa e previsione 90 giorni</div>
      <div className="card-immo p-4">
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <label className="text-sm">Saldo cassa di oggi (€)
            <EuroInput testid="cassa-saldo" value={saldo.saldo_attuale} onChange={(e) => setSaldo({ ...saldo, saldo_attuale: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--border)] text-sm" /></label>
          <label className="text-sm">Soglia di allarme (€)
            <EuroInput testid="cassa-soglia" value={saldo.soglia_allarme} onChange={(e) => setSaldo({ ...saldo, soglia_allarme: e.target.value })} className="w-full mt-1 px-3 py-2 rounded-lg border border-[var(--border)] text-sm" /></label>
        </div>
        <button data-testid="cassa-salva" onClick={salva} className="px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold mb-3">Salva cassa</button>
        {prev && (
          <div data-testid="cassa-previsione" className={`p-3 rounded-xl text-sm ${prev.sotto_soglia ? "bg-red-50 border border-red-200" : "bg-[#F7F7F8] border border-[var(--border)]"}`}>
            <div>Tra 90 giorni prevista: <b data-testid="cassa-prev-90">{prev.saldo_impostato ? euro(prev.saldo_fine) : "Saldo iniziale mancante – inseriscilo in Finanza"}</b></div>
            {prev.saldo_impostato && <div className="text-xs text-[var(--muted)] mt-1">Minimo previsto {euro(prev.min_saldo)} il {prev.min_data}{prev.sotto_soglia ? " · ⚠️ sotto la soglia!" : ""}</div>}
          </div>
        )}
      </div>
    </div>
  );
}

function ScadenziarioCard() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const load = () => api.get("/direzione/scadenze").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const crea = async () => { try { await api.post("/direzione/scadenze", form); toast.success("Scadenza aggiunta"); setForm(null); load(); } catch (e) { toast.error(e.response?.data?.detail || "Errore"); } };
  const chiudi = async (id) => { try { await api.post(`/direzione/scadenze/${id}/chiudi`); load(); } catch (e) { toast.error("Errore"); } };
  return (
    <div className="mt-6" data-testid="scadenziario-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)]"><ShieldCheck size={14} /> Scadenziario fiscale e amministrativo</div>
        <button data-testid="scad-nuova" onClick={() => setForm({ descrizione: "", categoria: "Fiscale", data_scadenza: "", importo: "", note: "" })} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold"><Plus size={13} /> Aggiungi</button>
      </div>
      {form && (
        <div className="card-immo p-4 mb-3" data-testid="scad-form">
          <div className="grid sm:grid-cols-2 gap-2">
            <input data-testid="scad-desc" placeholder="Descrizione * (es. F24, IVA)" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            <input data-testid="scad-cat" placeholder="Categoria" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            <input data-testid="scad-data" type="date" value={form.data_scadenza} onChange={(e) => setForm({ ...form, data_scadenza: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            <div><EuroInput testid="scad-importo" placeholder="Importo € (opzionale)" value={form.importo} onChange={(e) => setForm({ ...form, importo: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" /></div>
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setForm(null)} className="flex-1 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-sm">Annulla</button>
            <button data-testid="scad-salva" onClick={crea} className="flex-1 py-2 rounded-lg bg-[#111111] text-white text-sm font-semibold">Salva</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {list.filter((s) => s.stato === "aperta").length === 0 && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessuna scadenza aperta.</div>}
        {list.filter((s) => s.stato === "aperta").map((s) => (
          <div key={s.id} data-testid={`scad-${s.id}`} className="card-immo p-4 flex items-center justify-between gap-2 flex-wrap">
            <div><div className="text-sm font-semibold">{s.descrizione} · {s.data_scadenza}</div>
              <div className="text-xs text-[var(--muted)]">{s.categoria}{s.importo ? ` · ${euro(s.importo)}` : ""}</div></div>
            <button data-testid={`scad-chiudi-${s.id}`} onClick={() => chiudi(s.id)} className="px-2.5 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium">Segna fatta</button>
            <DeleteBtn tipo="scadenza_fiscale" id={s.id} label={`${s.descrizione}${s.importo ? ` – ${euro(s.importo)}` : ""} – ${s.data_scadenza}`} onDone={load} />
          </div>
        ))}
      </div>
    </div>
  );
}

function FattureCard() {
  const [d, setD] = useState({ da_emettere: [], emesse: [] });
  const [form, setForm] = useState(null);
  const load = () => api.get("/direzione/fatture").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const crea = async () => {
    try { await api.post("/direzione/fatture", form); toast.success("Fattura aggiunta all'elenco"); setForm(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const copiaGenya = (f) => {
    const txt = `Cliente: ${f.cliente}\nCF/P.IVA: ${f.cf_piva || ""}\nIndirizzo: ${f.indirizzo || ""}\nImporto: ${f.importo}\nDescrizione: ${f.descrizione || ""}\nPratica: ${f.pratica || ""}`;
    navigator.clipboard?.writeText(txt); toast.success("Dati copiati: incollali in Genya");
  };
  const emessa = async (f) => {
    const numero = window.prompt("Numero fattura emessa in Genya:"); if (!numero) return;
    const dataem = window.prompt("Data emissione (AAAA-MM-GG):", new Date().toISOString().slice(0, 10)); if (!dataem) return;
    try { await api.post(`/direzione/fatture/${f.id}/emessa`, { numero, data_emissione: dataem }); toast.success("Segnata come emessa"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  return (
    <div className="mt-6" data-testid="fatture-card">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[var(--muted)]"><FileSpreadsheet size={14} /> Fatture da emettere (Genya)</div>
        <button data-testid="fattura-nuova" onClick={() => setForm({ cliente: "", cf_piva: "", indirizzo: "", importo: "", descrizione: "", pratica: "" })} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold"><Plus size={13} /> Prepara dati</button>
      </div>
      {form && (
        <div className="card-immo p-4 mb-3" data-testid="fattura-form">
          <div className="grid sm:grid-cols-2 gap-2">
            {[["cliente", "Cliente *"], ["cf_piva", "CF / P.IVA"], ["indirizzo", "Indirizzo"], ["importo", "Importo € *"], ["descrizione", "Descrizione"], ["pratica", "Pratica"]].map(([k, l]) => k === "importo" ? (
              <div key={k}><EuroInput testid="fattura-importo" placeholder={l} value={form[k]} onChange={(e) => setForm({ ...form, importo: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[var(--border)] text-sm" /></div>
            ) : (
              <input key={k} data-testid={`fattura-${k}`} placeholder={l} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="px-3 py-2 rounded-lg border border-[var(--border)] text-sm" />
            ))}
          </div>
          <div className="flex gap-2 mt-2">
            <button onClick={() => setForm(null)} className="flex-1 py-2 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-sm">Annulla</button>
            <button data-testid="fattura-salva" onClick={crea} className="flex-1 py-2 rounded-lg bg-[#111111] text-white text-sm font-semibold">Salva</button>
          </div>
        </div>
      )}
      <div className="space-y-2">
        {d.da_emettere.length === 0 && <div className="card-immo p-6 text-center text-[var(--muted)] text-sm">Nessuna fattura da emettere.</div>}
        {d.da_emettere.map((f) => (
          <div key={f.id} data-testid={`fattura-${f.id}`} className="card-immo p-4 flex items-center justify-between gap-2 flex-wrap">
            <div><div className="text-sm font-semibold">{f.cliente} · {euro(f.importo)}</div>
              <div className="text-xs text-[var(--muted)]">{f.descrizione || f.pratica || "—"}</div></div>
            <div className="flex gap-1.5">
              <button data-testid={`fattura-copia-${f.id}`} onClick={() => copiaGenya(f)} className="px-2.5 py-1.5 rounded-lg bg-[#F7F7F8] border border-[var(--border)] text-xs font-medium">Copia dati per Genya</button>
              <button data-testid={`fattura-emessa-${f.id}`} onClick={() => emessa(f)} className="px-2.5 py-1.5 rounded-lg bg-[#FC82ED] text-black text-xs font-semibold">Emessa</button>
              <DeleteBtn tipo="fattura" id={f.id} label={`Fattura ${f.cliente} – ${euro(f.importo)}`} onDone={load} />
            </div>
          </div>
        ))}
        {d.emesse.slice(0, 5).map((f) => (
          <div key={f.id} className="card-immo p-3 flex items-center justify-between gap-2 opacity-70">
            <div className="text-sm">{f.cliente} · {euro(f.importo)}</div>
            <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">N.{f.numero} · {f.data_emissione}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
